import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import { buildPerson, buildBreadcrumbs } from "@/lib/jsonLd";

const STAGES = [
  {
    n: "01",
    year: "2005",
    title: "Entrée chez les Compagnons du Devoir",
    body:
      "Au lycée, un professeur de mathématiques passionné de menuiserie a parlé des Compagnons. Une rencontre qui a tout déclenché.",
  },
  {
    n: "02",
    year: "2007",
    title: "BEP Menuiserie fabrication agencement",
    body:
      "Formation aux Compagnons obtenue à la sortie du parcours. Le geste, la matière, la rigueur — déjà installés.",
  },
  {
    n: "03",
    year: "2007 — 2015",
    title: "Années d'apprentissage",
    body:
      "Huit années auprès d'autres artisans, à apprendre des chantiers, des contraintes, des matières et des clients.",
  },
  {
    n: "04",
    year: "2015",
    title: "Installation à son compte",
    body:
      "Artisan depuis 2015. Une exigence de qualité tenue projet après projet, dans la précision du geste comme dans la tenue des délais.",
  },
];

const PROMESSES = [
  "Devis lisible, ligne par ligne, sous huit jours ouvrés",
  "Décennale et RC Pro à jour — attestations transmises sans délai",
  "Délais tenus — pas de report opportuniste",
  "Reprise des jonctions à la main — parquet, lambris, carrelage",
  "Coordination directe avec votre cabinet d'expertise si sinistre",
  "Suivi par un seul interlocuteur du premier appel à la livraison",
];

const PROJET_TYPES = [
  {
    titre: "Cuisine sur-mesure",
    desc: "Plans de travail, façades en matières franches, finitions à la main.",
    href: "/contact?cible=particulier",
  },
  {
    titre: "Dressing & rangements",
    desc: "Volumes optimisés, mécanismes invisibles, calage au millimètre.",
    href: "/contact?cible=particulier",
  },
  {
    titre: "Bibliothèque & escalier",
    desc: "Pièces signature qui structurent un intérieur pour dix ans.",
    href: "/contact?cible=particulier",
  },
  {
    titre: "Remise en état après sinistre",
    desc: "Dossiers menés en coordination avec cabinets d'expertise.",
    href: "/cabinets-d-expertise",
  },
  {
    titre: "Agencement de restaurant",
    desc: "Calendrier compatible avec la reprise d'activité, banquettes, comptoirs.",
    href: "/contact?cible=professionnel",
  },
  {
    titre: "Boutique & commerce",
    desc: "Mobilier de vente, vitrines, agencement d'enseigne.",
    href: "/contact?cible=professionnel",
  },
];

export default function LeTravail() {
  return (
    <div data-testid="le-travail-page">
      <Seo
        title="Savoir-faire menuisier agenceur"
        description="Le savoir-faire de Luigi Chauveau : formé chez les Compagnons du Devoir, artisan depuis 2015. Quatre essences franches, méthode prévisible, délais tenus."
        path="/le-savoir-faire"
        jsonLd={[
          buildPerson(),
          buildBreadcrumbs([{ name: "Savoir-faire", path: "/le-savoir-faire" }]),
        ]}
      />
      {/* INTRO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-10 md:pb-14 grid md:grid-cols-12 gap-x-10 gap-y-8 items-end">
        <div className="md:col-span-7">
          <p className="text-eyebrow-gold mb-3">01 — Savoir-faire</p>
          <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight fade-up">
            Travailler le bois, <span className="italic text-primary">pour la durée</span>.
          </h1>
        </div>
        <p className="md:col-span-5 text-[14.5px] leading-relaxed text-foreground/75 max-w-md md:justify-self-end fade-up fade-up-1">
          Formé chez les Compagnons du Devoir. Artisan depuis 2015. Le geste
          précis, le calendrier tenu, et l'idée têtue qu'un agencement bien
          fait se voit dix ans après — pas dix jours après.
        </p>
      </section>

      {/* CE QUE JE FAIS — fond sombre éditorial pour casser le beige */}
      <section className="bg-foreground text-background" data-testid="savoir-faire-projets">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <p className="text-eyebrow text-[hsl(var(--gold))] mb-4">02 — Ce que je fais</p>
              <h2 className="h-display text-4xl md:text-5xl leading-tight">
                Six manières
                <br />
                <span className="italic text-[hsl(var(--gold-soft))]">d'entrer en chantier</span>.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-background/75 text-[15px] max-w-md leading-relaxed">
                Chaque projet a sa propre exigence — mais la méthode reste la
                même : écouter, mesurer, dessiner, livrer.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-background/15">
            {PROJET_TYPES.map((p, i) => (
              <Link
                key={p.titre}
                to={p.href}
                className="group bg-foreground p-7 md:p-9 min-h-[210px] flex flex-col justify-between hover:bg-[hsl(var(--gold))] hover:text-foreground transition-colors duration-500"
                data-testid={`projet-type-${i}`}
              >
                <span className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--gold))] group-hover:text-foreground/80">0{i + 1}</span>
                <div>
                  <h3 className="font-serif text-2xl md:text-[28px] leading-tight mb-3">{p.titre}</h3>
                  <p className="text-[13.5px] leading-relaxed opacity-75 mb-5">{p.desc}</p>
                  <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--gold-soft))] group-hover:text-foreground">
                    Parler de ce projet <ArrowUpRight size={12} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PARCOURS */}
      <section className="bg-muted/30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-5">
              <p className="text-eyebrow mb-4">03 — Parcours</p>
              <h2 className="h-display text-4xl md:text-5xl leading-tight">
                Une vocation
                <br />
                tenue depuis 2005.
              </h2>
              <p className="text-muted-foreground text-[15px] max-w-md mt-6 leading-relaxed">
                Du lycée jusqu'à l'installation, vingt ans d'apprentissage —
                aux Compagnons puis aux côtés d'autres artisans.
              </p>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <figure className="relative aspect-[4/3] overflow-hidden bg-foreground" data-testid="savoir-faire-parcours-image">
                <img
                  src="https://images.pexels.com/photos/5974340/pexels-photo-5974340.jpeg?auto=compress&cs=tinysrgb&w=1800"
                  alt="Mains d'un menuisier travaillant le bois au rabot — détail des copeaux"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <figcaption className="absolute left-0 bottom-0 right-0 px-5 py-3 bg-foreground/75 backdrop-blur-sm text-background text-[11px] tracking-[0.18em] uppercase">
                  Le geste avant l'image — un détail du travail au rabot
                </figcaption>
              </figure>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-y-2 gap-x-12 border-t border-border">
            {STAGES.map((s, i) => (
              <div
                key={s.n}
                className="py-10 border-b border-border grid grid-cols-12 gap-4"
                data-testid={`parcours-step-${i}`}
              >
                <span className="text-serial col-span-2 md:col-span-2 pt-1">{s.n}</span>
                <div className="col-span-10 md:col-span-10">
                  <p className="text-eyebrow text-primary mb-3">{s.year}</p>
                  <h3 className="font-serif text-2xl md:text-3xl mb-3 leading-tight">{s.title}</h3>
                  <p className="text-[14.5px] text-muted-foreground max-w-lg leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMESSES — trust + acquisition */}
      <section className="border-y border-border bg-background" data-testid="savoir-faire-promesses">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-eyebrow mb-4">04 — Ce qu'on vous garantit</p>
            <h2 className="h-display text-4xl md:text-5xl leading-tight">
              Six règles
              <br />
              <span className="italic">non négociables</span>.
            </h2>
            <p className="text-muted-foreground text-[14.5px] mt-6 max-w-sm leading-relaxed">
              Le minimum qu'un artisan doit à son client. Tenu projet après
              projet — particuliers, restaurateurs, cabinets d'expertise.
            </p>
            <Link to="/contact" className="btn-ink mt-10" data-testid="savoir-faire-cta-promesses">
              Démarrer un projet <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <ul className="space-y-px bg-border">
              {PROMESSES.map((p, i) => (
                <li key={i} className="bg-background p-6 md:p-7 flex items-start gap-5" data-testid={`promesse-${i}`}>
                  <CheckCircle2 size={20} className="text-primary mt-1 shrink-0" />
                  <span className="text-[15px] leading-snug">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CITATION — fond sombre éditorial */}
      <section className="bg-foreground text-background py-24 md:py-36" data-testid="le-travail-quote">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <p className="text-eyebrow text-[hsl(var(--gold))] mb-6">05 — Parole d'artisan</p>
          <blockquote className="h-display text-3xl sm:text-5xl md:text-[64px] leading-[1.05] text-balance">
            <span className="serif-italic text-[hsl(var(--gold-soft))]">«</span> Ce que je préfère aujourd'hui ? La satisfaction du client en fin de
            chantier, et le fait de travailler des{" "}
            <span className="italic text-[hsl(var(--gold-soft))]">matériaux nobles</span>.
            <span className="serif-italic text-[hsl(var(--gold-soft))]"> »</span>
          </blockquote>
          <div className="mt-12 flex items-center gap-5">
            <span className="block w-12 h-px bg-[hsl(var(--gold))]" />
            <p className="text-[12px] tracking-[0.25em] uppercase text-background/70">Luigi Chauveau · Artisan depuis 2015</p>
          </div>
        </div>
      </section>

      {/* CHANTIER */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src="https://images.pexels.com/photos/7492582/pexels-photo-7492582.jpeg"
              alt="Détail d'agencement bois — atelier Luigi Chauveau"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
        <div className="md:col-span-4 md:col-start-9 md:pt-10">
          <p className="text-eyebrow mb-4">06 — Méthode de chantier</p>
          <h2 className="h-display text-3xl md:text-4xl mb-6 leading-tight">
            Du relevé au dernier filet de silicone.
          </h2>
          <p className="text-[14.5px] text-muted-foreground leading-relaxed mb-4">
            Premier contact, visite, relevé au millimètre, devis lisible. Puis
            la fabrication, la pose, et la reprise des jonctions à la main.
          </p>
          <p className="text-[14.5px] text-muted-foreground leading-relaxed">
            Sur les dossiers de sinistre, coordination directe avec le cabinet
            d'expertise — constat sous cinq jours ouvrés, devis sous huit
            jours, livraison dans le calendrier convenu.
          </p>
          <Link to="/cabinets-d-expertise" className="text-eyebrow link-underline-static inline-block mt-8">
            Voir la page dédiée aux cabinets d'expertise →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-foreground text-background py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="text-eyebrow text-background/60 mb-4">09 — Engager une conversation</p>
            <h2 className="h-display text-4xl md:text-6xl leading-[1.02]">
              Un projet en tête,
              <br />
              <span className="italic">on commence par s'écouter</span>.
            </h2>
            <p className="mt-8 text-[15px] text-background/75 max-w-xl leading-relaxed">
              Une visite, un devis lisible, un calendrier qu'on tient. Réponse
              sous 48 heures ouvrées — sous cinq jours pour les sinistres.
            </p>
          </div>
          <div className="md:col-span-3 md:col-start-10 flex flex-col gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-primary text-background px-6 py-4 text-[12px] tracking-[0.16em] uppercase font-medium hover:bg-background hover:text-foreground transition-colors w-full justify-center"
              data-testid="le-travail-cta"
            >
              Demander un devis <ArrowUpRight size={14} />
            </Link>
            <a
              href="tel:+33761051061"
              className="text-[12px] tracking-[0.22em] uppercase link-underline-static text-background/85 text-center"
            >
              07 61 05 10 61
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
